import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  MapPin, Building2, DollarSign, TrendingUp, Calendar, 
  FileText, Lock, CheckCircle, AlertTriangle, ArrowLeft,
  Brain, RefreshCw, ShieldCheck, BarChart3, Heart, MessageSquare
} from 'lucide-react';
import { formatCurrency, getStageColor, getStatusColor } from '@/lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function DealDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [deal, setDeal] = useState(null);
  const [aiScore, setAiScore] = useState(null);
  const [scoring, setScoring] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDeal();
  }, [id]);

  const fetchDeal = async () => {
    try {
      const [dealRes, scoreRes] = await Promise.all([
        axios.get(`${API}/deals/${id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/deals/${id}/score`, { headers: getAuthHeaders() }).catch(() => ({ data: null }))
      ]);
      setDeal(dealRes.data);
      if (scoreRes.data) setAiScore(scoreRes.data);
    } catch (error) {
      console.error('Failed to fetch deal:', error);
      toast.error('Failed to load deal');
    } finally {
      setLoading(false);
    }
  };

  const runAIScoring = async () => {
    setScoring(true);
    try {
      const res = await axios.post(`${API}/deals/${id}/score`, {}, { headers: getAuthHeaders() });
      setAiScore(res.data);
      toast.success('AI scoring complete');
    } catch (error) {
      toast.error('Scoring failed');
    } finally {
      setScoring(false);
    }
  };

  const handleExpressInterest = async () => {
    try {
      await axios.post(`${API}/interest/express`, { entity_id: id, entity_type: 'deal', message: '' }, { headers: getAuthHeaders() });
      toast.success('Interest expressed successfully. Admin will be notified.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to express interest');
    }
  };

  const handleRequestInfo = async () => {
    try {
      await axios.post(`${API}/interest/request-info`, { entity_id: id, entity_type: 'deal', request_type: 'documents', message: 'Requesting additional documents and information' }, { headers: getAuthHeaders() });
      toast.success('Information request submitted. Admin will be notified.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const handleRequestAccess = async () => {
    try {
      await axios.post(
        `${API}/access-requests`,
        { deal_id: id, message: 'Requesting access to deal data room' },
        { headers: getAuthHeaders() }
      );
      toast.success('Access request submitted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">Loading deal...</div>
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">Deal not found</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="deal-detail-page">
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/deals')}
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-3xl font-bold tracking-tight">{deal.name}</h1>
            {deal.verification_status === 'verified' && (
              <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 text-[10px]" data-testid="verification-badge">Verified by INMX</Badge>
            )}
            {deal.verification_status === 'pending_verification' && (
              <Badge className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300 text-[10px]">Pending Verification</Badge>
            )}
            {deal.listing_type === 'demo' && (
              <Badge variant="outline" className="text-[10px] border-orange-300 text-orange-600" data-testid="demo-badge">Demo Listing</Badge>
            )}
          </div>
          <div className="flex items-center space-x-3 mt-2 text-muted-foreground">
            <div className="flex items-center space-x-1">
              <MapPin size={16} />
              <span>{deal.country}</span>
            </div>
            <span>&middot;</span>
            <span>{deal.sector}</span>
            <span>&middot;</span>
            <Badge className={getStatusColor(deal.status)}>
              {deal.status?.replace(/_/g, ' ')}
            </Badge>
          </div>
          {/* Deal Readiness Progress */}
          <div className="mt-3 max-w-md" data-testid="deal-readiness">
            {(() => {
              const stages = ['concept', 'feasibility', 'development', 'financial_close', 'operational'];
              const labels = { concept: 'Concept', feasibility: 'Feasibility', development: 'Development', financial_close: 'Fin. Close', operational: 'Operational' };
              const currentIdx = stages.indexOf(deal.stage);
              const progress = currentIdx >= 0 ? ((currentIdx + 1) / stages.length) * 100 : 0;
              return (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-muted-foreground">Deal Readiness</span>
                    <span className="text-[10px] font-mono text-muted-foreground capitalize">{labels[deal.stage] || deal.stage}</span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-accent rounded-full transition-all" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExpressInterest} variant="default" data-testid="express-interest-btn">
            <Heart size={16} className="mr-2" />
            Express Interest
          </Button>
          <Button onClick={handleRequestInfo} variant="outline" data-testid="request-info-btn">
            <MessageSquare size={16} className="mr-2" />
            Request Additional Info
          </Button>
          <Button onClick={handleRequestAccess} variant="outline" data-testid="request-access-button">
            <Lock size={16} className="mr-2" />
            Request Data Room Access
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="capital-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center">
              <DollarSign size={16} className="mr-2" />
              Capital Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono">{formatCurrency(deal.capital_required)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Equity: {deal.equity_split}% / Debt: {deal.debt_split}%
            </p>
          </CardContent>
        </Card>

        <Card data-testid="irr-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center">
              <TrendingUp size={16} className="mr-2" />
              Target IRR
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono">
              {deal.target_irr ? `${deal.target_irr}%` : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Expected return</p>
          </CardContent>
        </Card>

        <Card data-testid="stage-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center">
              <Calendar size={16} className="mr-2" />
              Project Stage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Badge className={`${getStageColor(deal.stage)} text-sm px-3 py-1`}>
              {deal.stage}
            </Badge>
            <p className="text-xs text-muted-foreground mt-2">Current status</p>
          </CardContent>
        </Card>

        <Card data-testid="score-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center">
              <Brain size={16} className="mr-2" />
              AI Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono">
              {aiScore ? `${aiScore.overall_score}/100` : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {aiScore ? aiScore.recommendation?.replace('_', ' ') : 'Not scored yet'}
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview" data-testid="overview-tab">Overview</TabsTrigger>
          <TabsTrigger value="ai-score" data-testid="ai-score-tab">AI Score</TabsTrigger>
          <TabsTrigger value="details" data-testid="details-tab">Details</TabsTrigger>
          <TabsTrigger value="risks" data-testid="risks-tab">Risk Assessment</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Project Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm leading-relaxed">{deal.summary}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Key Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Developer</p>
                <p className="text-sm mt-1">{deal.developer}</p>
              </div>
              <Separator />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Sector</p>
                <p className="text-sm mt-1">{deal.sector}</p>
              </div>
              <Separator />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Location</p>
                <p className="text-sm mt-1">{deal.country}</p>
              </div>
              <Separator />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Confidentiality Level</p>
                <Badge className="mt-1 capitalize">{deal.confidentiality_level}</Badge>
              </div>
              {deal.contract_status && (
                <>
                  <Separator />
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Contract Status</p>
                    <Badge variant="outline" className="mt-1 capitalize">{deal.contract_status?.replace(/_/g, ' ')}</Badge>
                  </div>
                </>
              )}
              {deal.spv_status && (
                <>
                  <Separator />
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">SPV Status</p>
                    <Badge variant="outline" className="mt-1 capitalize">{deal.spv_status?.replace(/_/g, ' ')}</Badge>
                  </div>
                </>
              )}
              {deal.existing_partners?.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Existing Partners Engaged</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {deal.existing_partners.map((p, i) => (
                        <Badge key={i} variant="secondary" className="text-xs capitalize">{p}</Badge>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ai-score" className="space-y-4">
          {!aiScore ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Brain size={48} className="mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">AI Deal Scoring</h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                  Run AI-powered analysis to score this deal on bankability, risk, market alignment, regulatory readiness, and execution capability.
                </p>
                <Button onClick={runAIScoring} disabled={scoring} data-testid="run-ai-score-btn">
                  <Brain size={16} className={`mr-2 ${scoring ? 'animate-pulse' : ''}`} />
                  {scoring ? 'Analyzing Deal...' : 'Run AI Scoring'}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <BarChart3 size={18} />
                      Score Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {[
                      { label: 'Bankability', value: aiScore.bankability_score, weight: '30%' },
                      { label: 'Risk Assessment', value: aiScore.risk_score, weight: '25%' },
                      { label: 'Market Alignment', value: aiScore.market_alignment_score, weight: '20%' },
                      { label: 'Regulatory', value: aiScore.regulatory_score, weight: '15%' },
                      { label: 'Execution', value: aiScore.execution_score, weight: '10%' }
                    ].map(item => (
                      <div key={item.label}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm">{item.label} <span className="text-xs text-muted-foreground">({item.weight})</span></span>
                          <span className="text-sm font-mono font-bold">{item.value}</span>
                        </div>
                        <Progress value={item.value} className="h-2" />
                      </div>
                    ))}
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">Overall Score</span>
                      <span className="text-2xl font-mono font-bold">{aiScore.overall_score}<span className="text-sm text-muted-foreground">/100</span></span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <ShieldCheck size={18} />
                      Recommendation
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="text-center py-3">
                      <Badge className={`text-base px-4 py-2 ${
                        aiScore.recommendation === 'strong_buy' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        aiScore.recommendation === 'buy' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                        aiScore.recommendation === 'hold' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                        aiScore.recommendation === 'cautious' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {aiScore.recommendation?.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </div>
                    <p className="text-sm leading-relaxed">{aiScore.summary}</p>

                    {aiScore.strengths?.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold mb-2 flex items-center gap-1"><CheckCircle size={14} className="text-green-500" />Strengths</p>
                        <ul className="space-y-1">
                          {aiScore.strengths.map((s, i) => <li key={i} className="text-sm text-muted-foreground flex items-start gap-2"><span className="text-green-500 mt-0.5">+</span>{s}</li>)}
                        </ul>
                      </div>
                    )}

                    {aiScore.risk_flags?.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold mb-2 flex items-center gap-1"><AlertTriangle size={14} className="text-yellow-500" />Risk Flags</p>
                        <ul className="space-y-1">
                          {aiScore.risk_flags.map((r, i) => <li key={i} className="text-sm text-muted-foreground flex items-start gap-2"><span className="text-yellow-500 mt-0.5">!</span>{r}</li>)}
                        </ul>
                      </div>
                    )}

                    <div className="text-xs text-muted-foreground pt-2">
                      Scored: {aiScore.scored_at ? new Date(aiScore.scored_at).toLocaleString() : 'N/A'}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="details" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Project Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Permits Status</p>
                <p className="text-sm mt-1">{deal.permits_status || 'Not specified'}</p>
              </div>
              <Separator />
              <div>
                <p className="text-sm font-medium text-muted-foreground">EPC Readiness</p>
                <Badge className="mt-1 capitalize">{deal.epc_readiness.replace('_', ' ')}</Badge>
              </div>
              <Separator />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Offtake Readiness</p>
                <Badge className="mt-1 capitalize">{deal.offtake_readiness.replace('_', ' ')}</Badge>
              </div>
              <Separator />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Views</p>
                <p className="text-sm mt-1 font-mono">{deal.views || 0} views</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="risks" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <AlertTriangle size={20} className="mr-2 text-yellow-500" />
                Risk Flags
              </CardTitle>
            </CardHeader>
            <CardContent>
              {deal.risk_flags && deal.risk_flags.length > 0 ? (
                <ul className="space-y-2">
                  {deal.risk_flags.map((risk, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <AlertTriangle size={16} className="text-yellow-500 mt-0.5" />
                      <span className="text-sm">{risk}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">No risk flags identified</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
